import React, { createContext, useContext, useState, useEffect } from 'react';
import API_BASE_URL from '../config/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [fieldPermissions, setFieldPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tokens, setTokens] = useState({
    access: localStorage.getItem('access_token'),
    refresh: localStorage.getItem('refresh_token')
  });

  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      const accessToken = localStorage.getItem('access_token');
      const refreshToken = localStorage.getItem('refresh_token');
      const userData = localStorage.getItem('user');

      if (accessToken && userData) {
        try {
          // Verify token is still valid
          const response = await fetch(`${API_BASE_URL}/api/auth/profile/`, {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
            },
          });

          if (response.ok) {
            const userInfo = JSON.parse(userData);
            const permissionsData = JSON.parse(localStorage.getItem('permissions') || '{}');
            const fieldPermissionsData = JSON.parse(localStorage.getItem('fieldPermissions') || '[]');
            setUser(userInfo);
            setPermissions(permissionsData);
            setFieldPermissions(fieldPermissionsData);
            setTokens({ access: accessToken, refresh: refreshToken });
          } else {
            // Token expired, try to refresh
            if (refreshToken) {
              await refreshAccessToken(refreshToken);
            } else {
              logout();
            }
          }
        } catch (error) {
          console.error('Auth check failed:', error);
          logout();
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const refreshAccessToken = async (refreshToken) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/refresh/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        const newAccessToken = data.access;
        
        localStorage.setItem('access_token', newAccessToken);
        setTokens(prev => ({ ...prev, access: newAccessToken }));
        return true;
      } else {
        logout();
        return false;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
      return false;
    }
  };

  const login = async (credentials) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('access_token', data.tokens.access);
        localStorage.setItem('refresh_token', data.tokens.refresh);
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem('permissions', JSON.stringify(data.permissions || {}));
        
        setUser(data.user);
        setPermissions(data.permissions || {});
        setTokens(data.tokens);
        
        // Fetch field-level permissions after successful login
        try {
          const fieldPermissionsResponse = await fetch(`${API_BASE_URL}/api/field-permissions/current-user/`, {
            headers: {
              'Authorization': `Bearer ${data.tokens.access}`,
            },
          });
          
          if (fieldPermissionsResponse.ok) {
            const fieldPermissionsData = await fieldPermissionsResponse.json();
            const fieldPermissionsList = fieldPermissionsData.field_permissions || [];
            localStorage.setItem('fieldPermissions', JSON.stringify(fieldPermissionsList));
            setFieldPermissions(fieldPermissionsList);
          }
        } catch (error) {
          console.error('Failed to fetch field permissions:', error);
        }
        
        return { success: true, data };
      } else {
        return { success: false, error: data.error || 'Login failed' };
      }
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  const logout = () => {
    // Clear localStorage
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    localStorage.removeItem('permissions');
    localStorage.removeItem('fieldPermissions');
    
    // Clear state
    setUser(null);
    setPermissions({});
    setFieldPermissions([]);
    setTokens({ access: null, refresh: null });
  };

  const getAuthHeaders = () => {
    const accessToken = localStorage.getItem('access_token');
    return accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {};
  };

  const makeAuthenticatedRequest = async (url, options = {}) => {
    const accessToken = localStorage.getItem('access_token');
    const refreshToken = localStorage.getItem('refresh_token');

    if (!accessToken) {
      throw new Error('No access token available');
    }

    // Add auth header
    const headers = {
      'Authorization': `Bearer ${accessToken}`,
      ...options.headers,
    };

    // Only add Content-Type for non-FormData requests
    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (response.status === 401 && refreshToken) {
        // Token expired, try to refresh
        const refreshSuccess = await refreshAccessToken(refreshToken);
        if (refreshSuccess) {
          // Retry the request with new token
          const newAccessToken = localStorage.getItem('access_token');
          headers.Authorization = `Bearer ${newAccessToken}`;
          
          const retryResponse = await fetch(url, {
            ...options,
            headers,
          });
          return retryResponse;
        }
      }

      return response;
    } catch (error) {
      console.error('Authenticated request failed:', error);
      throw error;
    }
  };

  const refreshFieldPermissions = async () => {
    try {
      const accessToken = localStorage.getItem('access_token');
      if (!accessToken) {
        console.error('No access token available for field permissions refresh');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/field-permissions/current-user/`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      
      if (response.ok) {
        const fieldPermissionsData = await response.json();
        const fieldPermissionsList = fieldPermissionsData.field_permissions || [];
        localStorage.setItem('fieldPermissions', JSON.stringify(fieldPermissionsList));
        setFieldPermissions(fieldPermissionsList);
        console.log('Field permissions refreshed successfully');
      } else {
        console.error('Failed to refresh field permissions:', response.status);
      }
    } catch (error) {
      console.error('Error refreshing field permissions:', error);
    }
  };

  const value = {
    user,
    permissions,
    fieldPermissions,
    loading,
    tokens,
    login,
    logout,
    getAuthHeaders,
    makeAuthenticatedRequest,
    refreshFieldPermissions,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
